import { ReactNode, useState } from 'react';
import { FiPackage, FiSearch, FiEye } from 'react-icons/fi';
import type { OrderItem } from '../../../../../types/apiResponse/order';
import { itemFinancials } from './financials';
import { Translate } from './messages';
import { text } from './presentation';
import * as s from './styles';

type Props = {
  items: OrderItem[] | undefined;
  decimals: number;
  l: Translate;
  money: (value: unknown) => string;
  decimal: (value: unknown) => string;
  itemDetails: (item: OrderItem) => ReactNode;
  compact?: boolean;
};

export default function OrderItems({
  items,
  decimals,
  l,
  money,
  decimal,
  itemDetails,
  compact = false,
}: Props) {
  const [search, setSearch] = useState('');
  if (!items?.length) return <s.Hint>{l(items ? 'noItems' : 'unavailableItems')}</s.Hint>;
  const filtered = items.filter((item) =>
    [
      item.CodigoProduto,
      item.productDescription,
      item.DescricaoProduto,
      item.product?.Descricao,
      item.embalagem,
      item.CodigoEmbalagem,
      item.CodigoPromocao,
    ].some((value) => text(value).toLowerCase().includes(search.trim().toLowerCase())),
  );

  const discount = (item: OrderItem) => {
    const price = itemFinancials(item, decimals);
    return (
      <>
        <span className="discount">
          {l('discount')}:{' '}
          {decimal(price.discount === null ? null : price.discount ? price.adjustmentPercent : 0)}%
          ({price.discount ? '−' : ''}
          {money(price.discount)})
        </span>
        {!!price.surcharge && (
          <span className="surcharge">
            {l('surcharge')}: {decimal(price.adjustmentPercent)}% (+{money(price.surcharge)})
          </span>
        )}
      </>
    );
  };

  const product = (item: OrderItem) => (
    <>
      <strong className="product-name">
        {text(item.productDescription || item.DescricaoProduto || item.product?.Descricao) ||
          item.CodigoProduto}
      </strong>
      <span className="product-code">
        {l('productCode')}: {item.CodigoProduto}
        {!compact &&
          text(item.embalagem || item.CodigoEmbalagem) &&
          ` · ${item.embalagem || item.CodigoEmbalagem}`}
      </span>
    </>
  );
  const details = (item: OrderItem) => (
    <details className="item-extra">
      <summary aria-label={`${l('itemDetails')} · ${item.CodigoProduto}`} title={l('itemDetails')}>
        <FiEye size={18} aria-hidden="true" />
      </summary>
      {itemDetails(item)}
    </details>
  );

  if (compact)
    return (
      <>
        <s.ItemCount>
          <span>
            {items.length} {l('productsCount')}
          </span>
          <span>
            {decimal(items.reduce((sum, item) => sum + (Number(item.QtdVenda) || 0), 0))}{' '}
            {l('unitsCount')}
          </span>
        </s.ItemCount>
        <s.SummaryTable>
          <table>
            <thead>
              <tr>
                {(['product', 'summaryQuantity', 'unitPrice', 'lineTotal'] as const).map(
                  (label) => (
                    <th scope="col" key={label}>
                      {l(label)}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const price = itemFinancials(item, decimals);
                return (
                  <tr key={`${item.itenspedido_id ?? item.CodigoProduto}-${index}`}>
                    <td>
                      {product(item)}
                      <div className="adjustments">{discount(item)}</div>
                    </td>
                    <td data-label={l('summaryQuantity')}>{decimal(price.quantity)}</td>
                    <td data-label={l('unitPrice')}>
                      <span className="unit-prices">
                        {(!!price.discount || !!price.surcharge) && (
                          <del>{money(price.tablePrice)}</del>
                        )}
                        <span>{money(price.netPrice)}</span>
                      </span>
                    </td>
                    <td data-label={l('lineTotal')}>
                      <strong>{money(price.netTotal)}</strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </s.SummaryTable>
      </>
    );

  return (
    <>
      <s.Search>
        <FiSearch aria-hidden="true" />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={l('search')}
          aria-label={l('search')}
        />
      </s.Search>
      <s.Hint role="status">
        {l('recordedItems')}: {filtered.length} / {items.length}
      </s.Hint>
      <s.ItemList>
        {filtered.map((item, index) => {
          const price = itemFinancials(item, decimals);
          return (
            <s.ItemCard key={`${item.itenspedido_id ?? item.CodigoProduto}-${index}`}>
              <div className="item-heading">
                <span className="product-icon">
                  <FiPackage size={26} aria-hidden="true" />
                </span>
                <div className="item-main">
                  {product(item)}
                  <div className="item-equation">
                    <strong>{money(price.netTotal)}</strong>
                    {!!price.discount && (
                      <span className="discount">−{decimal(price.adjustmentPercent)}%</span>
                    )}
                    {!!price.surcharge && (
                      <span className="surcharge">+{decimal(price.adjustmentPercent)}%</span>
                    )}
                    <span>
                      = {money(price.basePrice)} × {decimal(price.quantity)}
                    </span>
                    {price.discountPercent > 0 && (
                      <span>
                        − {decimal(price.discountPercent)}% {l('itemDiscountShort')}
                      </span>
                    )}
                  </div>
                </div>
                {details(item)}
              </div>
            </s.ItemCard>
          );
        })}
      </s.ItemList>
      {!filtered.length && <s.Hint>{l('noResults')}</s.Hint>}
    </>
  );
}
